import * as XLSX from 'xlsx';

/**
 * Utility functions for exporting data to Excel spreadsheets
 */

/**
 * Helper function to get dewar and rack information from gridLocation
 * @param {Object} sample - Sample object with gridLocation
 * @returns {Object} Location details object
 */
const getLocationDetails = (sample) => {
  if (!sample?.gridLocation) {
    return {
      dewarName: 'N/A',
      rackName: 'N/A',
      boxName: 'N/A',
      position: 'N/A'
    };
  }

  const savedDewars = localStorage.getItem('dewars');
  if (!savedDewars) {
    return {
      dewarName: 'Unknown',
      rackName: 'Unknown',
      boxName: sample?.gridLocation?.boxName || 'Unknown',
      position: sample?.gridLocation?.position || 'Unknown'
    };
  }

  try {
    const dewarsData = JSON.parse(savedDewars);
    const dewar = dewarsData?.find(d => d?.id === sample?.gridLocation?.dewarId);
    
    if (!dewar) {
      return {
        dewarName: 'Not Found',
        rackName: 'Not Found',
        boxName: sample?.gridLocation?.boxName || 'Unknown',
        position: sample?.gridLocation?.position || 'Unknown'
      };
    }

    const rack = dewar?.racks?.find(r => r?.id === sample?.gridLocation?.rackId);
    
    return {
      dewarName: dewar?.name || 'Unknown',
      rackName: rack?.name || 'Unknown',
      boxName: sample?.gridLocation?.boxName || 'Unknown',
      position: sample?.gridLocation?.position || 'Unknown'
    };
  } catch (error) {
    console.error('Error parsing dewars data:', error);
    return {
      dewarName: 'Error',
      rackName: 'Error',
      boxName: sample?.gridLocation?.boxName || 'Unknown',
      position: sample?.gridLocation?.position || 'Unknown'
    };
  }
};

/**
 * Export samples data to Excel
 * @param {Array} samples - Array of sample objects
 * @param {string} filename - Optional filename (default: 'samples_export')
 */
export const exportSamplesToExcel = (samples, filename = 'samples_export') => {
  if (!samples || samples?.length === 0) {
    alert('No sample data available to export');
    return;
  }

  // Transform sample data for Excel with location details
  const excelData = samples?.map(sample => {
    const location = getLocationDetails(sample);
    return {
      'Sample ID': sample?.id || '',
      'Name': sample?.name || '',
      'Dewar': location?.dewarName,
      'Rack': location?.rackName,
      'Box': location?.boxName,
      'Box Position': location?.position,
      'Freeze Date': sample?.freezeDate || '',
      'Frozen By': sample?.frozenBy || '',
      'PI': sample?.pi || '',
      'Passage Number': sample?.passageNumber || '',
      'Cap Colour': sample?.capColour || '',
      'Notes': sample?.notes || ''
    };
  });

  // Create worksheet
  const worksheet = XLSX?.utils?.json_to_sheet(excelData);

  // Set column widths for better readability
  worksheet['!cols'] = [
    { wch: 12 }, // Sample ID
    { wch: 20 }, // Name
    { wch: 15 }, // Dewar
    { wch: 15 }, // Rack
    { wch: 15 }, // Box
    { wch: 12 }, // Box Position
    { wch: 12 }, // Freeze Date
    { wch: 15 }, // Frozen By
    { wch: 15 }, // PI
    { wch: 15 }, // Passage Number
    { wch: 12 }, // Cap Colour
    { wch: 40 }  // Notes
  ];

  // Create workbook and add worksheet
  const workbook = XLSX?.utils?.book_new();
  XLSX?.utils?.book_append_sheet(workbook, worksheet, 'Samples');

  // Generate filename with timestamp
  const timestamp = new Date()?.toISOString()?.split('T')?.[0];
  const finalFilename = `${filename}_${timestamp}.xlsx`;

  // Save file
  XLSX?.writeFile(workbook, finalFilename);
};

/**
 * Export dewars data to Excel with multiple sheets
 * @param {Array} dewars - Array of dewar objects
 * @param {string} filename - Optional filename (default: 'dewars_export')
 * @param {Object} dateRange - Optional date range {startDate, endDate} for filtering history
 */
export const exportDewarsToExcel = (dewars, filename = 'dewars_export', dateRange = null) => {
  if (!dewars || dewars?.length === 0) {
    alert('No dewar data available to export');
    return;
  }

  const workbook = XLSX?.utils?.book_new();

  // Sheet 1: Dewars Summary
  const dewarsSummary = dewars?.map(dewar => ({
    'Dewar ID': dewar?.id || '',
    'Name': dewar?.name || '',
    'Location': dewar?.location || '',
    'Temperature': dewar?.temp || '',
    'Status': dewar?.status || '',
    'Liquid N2 Level': `${dewar?.liquidNitrogenLevel || 0}"`,
    'Total Racks': dewar?.racks?.length || 0,
    'Total Boxes': dewar?.racks?.reduce((sum, rack) => sum + (rack?.boxes?.length || 0), 0) || 0,
    'Total Samples': dewar?.racks?.reduce((rackSum, rack) => {
      return rackSum + (rack?.boxes?.reduce((boxSum, box) => boxSum + (box?.occupied || 0), 0) || 0);
    }, 0) || 0
  }));

  const summarySheet = XLSX?.utils?.json_to_sheet(dewarsSummary);
  summarySheet['!cols'] = [
    { wch: 10 }, { wch: 15 }, { wch: 20 }, { wch: 12 }, 
    { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 }
  ];
  XLSX?.utils?.book_append_sheet(workbook, summarySheet, 'Dewars Summary');

  // Sheet 2: All Samples from all Dewars
  const allSamplesData = [];

  dewars?.forEach(dewar => {
    dewar?.racks?.forEach(rack => {
      rack?.boxes?.forEach(box => {
        box?.samples?.forEach(sample => {
          allSamplesData?.push({
            'Name': sample?.name || '',
            'Dewar': dewar?.name || '',
            'Rack': rack?.name || '',
            'Box': box?.name || '',
            'Position': sample?.position || '',
            'Freeze Date': sample?.freezeDate || '',
            'Frozen By': sample?.frozenBy || '',
            'PI': sample?.pi || '',
            'Passage Number': sample?.passageNumber || '',
            'Cap Color': sample?.capColor || '',
            'Notes': sample?.notes || ''
          });
        });
      });
    });
  });

  if (allSamplesData?.length > 0) {
    const samplesSheet = XLSX?.utils?.json_to_sheet(allSamplesData);
    samplesSheet['!cols'] = [
      { wch: 20 }, { wch: 15 }, { wch: 10 },
      { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 40 }
    ];
    XLSX?.utils?.book_append_sheet(workbook, samplesSheet, 'All Samples');
  }

  // Sheet 3: Nitrogen Level History with optional date filtering
  const nitrogenHistory = [];

  // Parse date range if provided
  const startDate = dateRange?.startDate ? new Date(dateRange.startDate) : null;
  const endDate = dateRange?.endDate ? new Date(dateRange.endDate) : null;
  // Set end date to end of day
  if (endDate) {
    endDate.setHours(23, 59, 59, 999);
  }

  dewars?.forEach(dewar => {
    dewar?.levelHistory?.forEach(entry => {
      const entryDate = new Date(entry?.date);

      // Filter by date range if provided
      if (startDate && entryDate < startDate) return;
      if (endDate && entryDate > endDate) return;

      nitrogenHistory?.push({
        'Dewar': dewar?.name || '',
        'Date': entryDate.toLocaleString() || '',
        'Previous Level': entry?.previousLevel !== null ? `${entry?.previousLevel}"` : 'N/A',
        'New Level': `${entry?.level}"`,
        'Change': entry?.previousLevel !== null ? `${(entry?.level - entry?.previousLevel)?.toFixed(1)}"` : 'Initial',
        'Action': entry?.action || '',
        'Checked By': entry?.checkedBy || '',
        'Notes': entry?.notes || ''
      });
    });
  });

  if (nitrogenHistory?.length > 0) {
    const historySheet = XLSX?.utils?.json_to_sheet(nitrogenHistory);
    historySheet['!cols'] = [
      { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 12 },
      { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 40 }
    ];
    XLSX?.utils?.book_append_sheet(workbook, historySheet, 'N2 Level History');
  } else if (dateRange?.startDate || dateRange?.endDate) {
    // Add a note if no data found in date range
    const noDataSheet = XLSX?.utils?.json_to_sheet([{
      'Message': 'No nitrogen level history found in the selected date range'
    }]);
    XLSX?.utils?.book_append_sheet(workbook, noDataSheet, 'N2 Level History');
  }

  // Generate filename with timestamp and optional date range
  const timestamp = new Date()?.toISOString()?.split('T')?.[0];
  const dateRangeSuffix = dateRange?.startDate || dateRange?.endDate
    ? `_${dateRange?.startDate || 'all'}_to_${dateRange?.endDate || 'all'}`
    : '';
  const finalFilename = `${filename}${dateRangeSuffix}_${timestamp}.xlsx`;

  // Save file
  XLSX?.writeFile(workbook, finalFilename);
};

/**
 * Export specific dewar data to Excel
 * @param {Object} dewar - Single dewar object
 * @param {string} filename - Optional filename
 */
export const exportSingleDewarToExcel = (dewar, filename) => {
  if (!dewar) {
    alert('No dewar data available to export');
    return;
  }

  const defaultFilename = `dewar_${dewar?.name?.replace(/\s+/g, '_')}_export`;
  exportDewarsToExcel([dewar], filename || defaultFilename);
};

/**
 * Export Occupancy Report - Focus on capacity and utilization
 * @param {Array} dewars - Array of dewar objects
 * @param {string} filename - Optional filename
 */
export const exportOccupancyReport = (dewars, filename = 'occupancy_report') => {
  if (!dewars || dewars?.length === 0) {
    alert('No dewar data available to export');
    return;
  }

  const workbook = XLSX?.utils?.book_new();

  // Dewar Utilization Summary
  const dewarUtilization = dewars?.map(dewar => {
    const totalCapacity = dewar?.racks?.reduce((rackSum, rack) => {
      return rackSum + (rack?.boxes?.reduce((boxSum, box) => boxSum + (box?.capacity || 0), 0) || 0);
    }, 0) || 0;

    const totalOccupied = dewar?.racks?.reduce((rackSum, rack) => {
      return rackSum + (rack?.boxes?.reduce((boxSum, box) => boxSum + (box?.occupied || 0), 0) || 0);
    }, 0) || 0;

    const utilizationPercent = totalCapacity > 0 ? ((totalOccupied / totalCapacity) * 100).toFixed(1) : 0;

    return {
      'Dewar': dewar?.name || '',
      'Location': dewar?.location || '',
      'Total Capacity': totalCapacity,
      'Occupied Slots': totalOccupied,
      'Available Slots': totalCapacity - totalOccupied,
      'Utilization %': `${utilizationPercent}%`,
      'Status': dewar?.status || '',
      'Liquid N2 Level': `${dewar?.liquidNitrogenLevel || 0}"`,
      'Total Racks': dewar?.racks?.length || 0,
      'Total Boxes': dewar?.racks?.reduce((sum, rack) => sum + (rack?.boxes?.length || 0), 0) || 0
    };
  });

  const utilizationSheet = XLSX?.utils?.json_to_sheet(dewarUtilization);
  utilizationSheet['!cols'] = [
    { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
    { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
    { wch: 12 }, { wch: 12 }
  ];
  XLSX?.utils?.book_append_sheet(workbook, utilizationSheet, 'Dewar Utilization');

  // Box-level details
  const boxDetails = [];
  dewars?.forEach(dewar => {
    dewar?.racks?.forEach(rack => {
      rack?.boxes?.forEach(box => {
        const capacity = box?.capacity || 0;
        const occupied = box?.occupied || 0;
        const available = capacity - occupied;
        const utilization = capacity > 0 ? ((occupied / capacity) * 100).toFixed(1) : 0;

        boxDetails.push({
          'Dewar': dewar?.name || '',
          'Rack': rack?.name || '',
          'Box': box?.name || '',
          'Type': box?.type || '',
          'Capacity': capacity,
          'Occupied': occupied,
          'Available': available,
          'Utilization %': `${utilization}%`
        });
      });
    });
  });

  if (boxDetails.length > 0) {
    const boxSheet = XLSX?.utils?.json_to_sheet(boxDetails);
    boxSheet['!cols'] = [
      { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }
    ];
    XLSX?.utils?.book_append_sheet(workbook, boxSheet, 'Box Details');
  }

  const timestamp = new Date()?.toISOString()?.split('T')?.[0];
  const finalFilename = `${filename}_${timestamp}.xlsx`;
  XLSX?.writeFile(workbook, finalFilename);
};

/**
 * Export Nitrogen Level Report - Focus on temperature monitoring
 * @param {Array} dewars - Array of dewar objects
 * @param {string} filename - Optional filename
 * @param {Object} dateRange - Optional date range {startDate, endDate}
 */
export const exportNitrogenLevelReport = (dewars, filename = 'nitrogen_level_report', dateRange = null) => {
  if (!dewars || dewars?.length === 0) {
    alert('No dewar data available to export');
    return;
  }

  const workbook = XLSX?.utils?.book_new();

  // Current Levels Summary
  const currentLevels = dewars?.map(dewar => {
    const level = dewar?.liquidNitrogenLevel || 0;
    let status = 'Good';
    if (level <= 4) status = 'Critical';
    else if (level <= 8) status = 'Low';

    return {
      'Dewar': dewar?.name || '',
      'Location': dewar?.location || '',
      'Current Level (inches)': level,
      'Status': status,
      'Temperature': dewar?.temp || '',
      'Last Updated': dewar?.updatedAt ? new Date(dewar?.updatedAt).toLocaleString() : ''
    };
  });

  const currentSheet = XLSX?.utils?.json_to_sheet(currentLevels);
  currentSheet['!cols'] = [
    { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 18 }
  ];
  XLSX?.utils?.book_append_sheet(workbook, currentSheet, 'Current Levels');

  // Full History with optional date filtering
  const nitrogenHistory = [];

  // Parse date range if provided
  const startDate = dateRange?.startDate ? new Date(dateRange.startDate) : null;
  const endDate = dateRange?.endDate ? new Date(dateRange.endDate) : null;
  // Set end date to end of day
  if (endDate) {
    endDate.setHours(23, 59, 59, 999);
  }

  dewars?.forEach(dewar => {
    dewar?.levelHistory?.forEach(entry => {
      const entryDate = new Date(entry?.date);

      // Filter by date range if provided
      if (startDate && entryDate < startDate) return;
      if (endDate && entryDate > endDate) return;

      nitrogenHistory.push({
        'Dewar': dewar?.name || '',
        'Date & Time': entryDate.toLocaleString() || '',
        'Previous Level (inches)': entry?.previousLevel !== null ? entry?.previousLevel : 'N/A',
        'New Level (inches)': entry?.level,
        'Change (inches)': entry?.previousLevel !== null ? (entry?.level - entry?.previousLevel)?.toFixed(1) : 'Initial',
        'Action': entry?.action || '',
        'Checked By': entry?.checkedBy || '',
        'Notes': entry?.notes || ''
      });
    });
  });

  if (nitrogenHistory.length > 0) {
    const historySheet = XLSX?.utils?.json_to_sheet(nitrogenHistory);
    historySheet['!cols'] = [
      { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 18 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 40 }
    ];
    XLSX?.utils?.book_append_sheet(workbook, historySheet, 'Full History');
  } else if (dateRange?.startDate || dateRange?.endDate) {
    // Add a note if no data found in date range
    const noDataSheet = XLSX?.utils?.json_to_sheet([{
      'Message': 'No nitrogen level history found in the selected date range'
    }]);
    XLSX?.utils?.book_append_sheet(workbook, noDataSheet, 'Full History');
  }

  const timestamp = new Date()?.toISOString()?.split('T')?.[0];
  const dateRangeSuffix = dateRange?.startDate || dateRange?.endDate
    ? `_${dateRange?.startDate || 'all'}_to_${dateRange?.endDate || 'all'}`
    : '';
  const finalFilename = `${filename}${dateRangeSuffix}_${timestamp}.xlsx`;
  XLSX?.writeFile(workbook, finalFilename);
};